import {
  ArrowUpCircleIcon,
  CheckIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useList from "common/hooks/useList";
import UserSearch from "components/search/UserSearch";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Conversation } from "types/Conversation";
import useRouter from "common/hooks/use-router";
import WorkSiteIcon from "components/Icon/icons/WorkSiteIcon";
import CategorySelect from "components/select/CategorySelect/CategorySelect";
import useConversation from "common/hooks/useConversation";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import AlertsContext from "common/providers/alerts";
import { FaExchangeAlt } from "react-icons/fa";
import { BiConversation } from "react-icons/bi";
import ConversationItem from "./ConversationItem";

function Conversations() {
  const { t } = useTranslation();

  const { setAlert } = useContext(AlertsContext);
  const { navigate } = useRouter();

  const { conversationsNumber, refreshConversationsNumber } = useConversation();

  const {
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Conversation>("conversations", {
    defaultSorts: ["updatedAt:DESC", "createdAt:DESC"],
    populate: [
      "content",
      "content.images",
      "content.category",
      "applicantProfile",
      "providerProfile",
      "finalMeetingPlace",
    ],
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: conversationsNumber?.all || 0,
        icon: ListBulletIcon,
        color: "primary",
      },
      {
        id: "started",
        name: t("status.started"),
        count: conversationsNumber?.started || 0,
        icon: ArrowUpCircleIcon,
        color: "blue",
      },
      {
        id: "established",
        name: t("status.established"),
        count: conversationsNumber?.established || 0,
        icon: ArrowUpCircleIcon,
        color: "blue",
      },
      {
        id: "validated",
        name: t("status.validated"),
        count: conversationsNumber?.validated || 0,
        icon: FaExchangeAlt,
        color: "green",
      },
      {
        id: "cancelled",
        name: t("status.cancelled"),
        count: conversationsNumber?.cancelled || 0,
        icon: FaExchangeAlt,
        color: "primary",
      },
      {
        id: "reviewed",
        name: t("status.reviewed"),
        count: conversationsNumber?.reviewed || 0,
        icon: CheckIcon,
        color: "darkgreen",
      },
    ];
  }, [t, conversationsNumber]);

  const columns = useMemo(
    () => [
      {
        Header: t("common.provider"),
        filterName: "applicantProfile][username]$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      },
      {
        Header: t("common.applicant"),
        filterName: "providerProfile][username]$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      },
      {
        Header: t("forms.category"),
        filterName: "content][category]$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <CategorySelect isFilter Icon={WorkSiteIcon} {...props} />
        ),
      },
      {
        Header: t("common.content"),
        filterName: "content][title]$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
      },
    ],
    [t]
  );

  useEffect(() => {
    refreshConversationsNumber();
  }, [refreshConversationsNumber]);

  const handleNavigate = (id: any) => {
    navigate(`/conversations/edit/${id}`);
  };

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.conversations")}
        subTitle={t("titleLegends.conversations")}
        Icon={BiConversation}
        noAdd
        isBeforeFilters
        filters={filters}
        setFilter={setFilter}
        filterName="status$containsi"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={ConversationItem}
        onDelete={() => {}}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Conversations;
